// The chat's card for a suggested standard (lib/standardSuggest.js): what it
// found, a way to check more findings, marking hits it shouldn't match and
// asking to refine it, a verdict on it, and saving it.
import { useContext, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { MORE_CHECK, checkWording, getSuggestContext, moreLabel, nextToCheck, subscribeSuggestContext } from "../lib/standardSuggest.js";
import { CARD, CARD_BUTTON as BUTTON, CARD_MUTED as MUTED, CARD_PICKED as PICKED, ChatReplyContext, useKept } from "./ChatQuestionCard.jsx";
import { FILE_LABEL } from "./lenses/triageParts.jsx";
import Checkbox from "./ui/Checkbox.jsx";
import { FIELD } from "./ui/styles.js";

const SHOWN = 5;
// A share of findings met above which the card says the standard may flag a lot.
const BUSY_RATE = 0.2;

const useSuggestContext = () => useSyncExternalStore(subscribeSuggestContext, getSuggestContext, () => null);

// What Refine sends: the standard as it stands, and the findings the person
// said it should not have matched, by id so the refined one is checked on them.
export function refineRequest({ name, text, wrong = [] }) {
    const lines = [`Refine "${name}":`, text, ""];
    if (wrong.length) {
        lines.push(`It matched ${wrong.length === 1 ? "this finding" : "these findings"}, and it shouldn't:`);
        for (const w of wrong) lines.push(`- ${w.title || "Untitled finding"} [id ${w.id}]${w.why ? `: ${w.why}` : ""}`);
        lines.push("");
    }
    lines.push("Suggest a few ways to refine it for me to pick from.");
    return lines.join("\n");
}

export function StandardSuggestionCard({ nodeId, details }) {
    const ctx = useSuggestContext();
    const { reply, busy } = useContext(ChatReplyContext);
    const [state, setState] = useKept(nodeId, { extra: [], extraIds: [], verdict: null, saved: null, backfilled: false, wrong: [], refined: false });
    const [running, setRunning] = useState(null);
    const [all, setAll] = useState(false);
    const [name, setName] = useState(details?.name || "");
    const [backfill, setBackfill] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const controller = useRef(null);
    useEffect(() => () => controller.current?.abort(), []);

    const text = String(details?.text || "");
    const results = [...(details?.results || []), ...state.extra];
    const checkedIds = [...(details?.checkedIds || []), ...state.extraIds];
    const checked = (Number(details?.checked) || 0) + state.extraIds.length;
    // The tab's count, once it has its findings again; until then the count the
    // check was made against.
    const total = Math.max(ctx?.ids?.length || 0, Number(details?.total) || 0, checked);
    const met = results.filter((r) => r.outcome === "met");
    const failed = results.filter((r) => r.outcome === "error").length;
    const windowName = ctx?.windowName || details?.windowName || "this window";
    const savedItem = state.saved || ctx?.standards?.find((s) => String(s.text || "").trim() === text.trim()) || null;
    const left = ctx ? nextToCheck(ctx.ids, checkedIds, MORE_CHECK).length : 0;
    // The findings an earlier version matched and the person said it shouldn't,
    // asked again of this wording.
    const recheck = Array.isArray(details?.recheck) ? details.recheck : [];
    const wrong = met.filter((r) => state.wrong.includes(r.id));
    const toggleWrong = (id) => setState((s) => ({ ...s, wrong: s.wrong.includes(id) ? s.wrong.filter((w) => w !== id) : [...s.wrong, id] }));
    const refine = () => {
        if (!reply || busy || state.refined) return;
        setState((s) => ({ ...s, refined: true }));
        reply(refineRequest({ name: details?.name || "", text, wrong }));
    };

    const checkMore = async () => {
        if (!ctx || running) return;
        const ids = nextToCheck(ctx.ids, checkedIds, MORE_CHECK);
        if (!ids.length) return;
        controller.current?.abort();
        const ctl = new AbortController();
        controller.current = ctl;
        setError("");
        setRunning({ done: 0, total: ids.length });
        try {
            const answered = [];
            await checkWording({
                text, ids, runWindow: ctx.readWindow, signal: ctl.signal,
                // An answer counts as checked as it lands, so what was found and
                // what it was found in move together.
                onAnswer: (r) => {
                    answered.push(r.id);
                    setRunning({ done: answered.length, total: ids.length });
                    setState((s) => ({ ...s, extra: [...s.extra, r], extraIds: [...s.extraIds, r.id] }));
                },
            });
            // A finding whose record could not be read is checked too: asking
            // again finds nothing. Stopped, only what was answered counts.
            if (!ctl.signal.aborted) {
                const unread = ids.filter((id) => !answered.includes(id));
                if (unread.length) setState((s) => ({ ...s, extraIds: [...s.extraIds, ...unread] }));
            }
        } catch (e) {
            if (!ctl.signal.aborted) setError(String(e?.message || e).slice(0, 200));
        } finally {
            setRunning(null);
        }
    };
    const stop = () => controller.current?.abort();

    const save = async () => {
        if (!ctx?.canWrite || saving || !name.trim()) return;
        setSaving(true);
        setError("");
        const item = await ctx.create(name.trim(), text, true);
        if (item) {
            const check = backfill && ctx.runWindow ? await ctx.backfill(item, ctx.runWindow) : null;
            setState((s) => ({ ...s, saved: item, backfilled: Boolean(check?.run) }));
            if (check?.error) setError(`Saved, but older findings weren't checked: ${check.error}`);
        } else {
            setError("Couldn't save it. The Triage tab says why.");
        }
        setSaving(false);
    };

    if (state.verdict === "not") {
        return (
            <div data-suggest-standard="" data-suggest-verdict="not" className="flex items-baseline gap-[8px] text-[12px] leading-[1.4] text-[color:var(--muted)]">
                <span className="min-w-0 truncate">{details?.name}</span>
                <span>{"·"} not useful</span>
                <button type="button" onClick={() => setState((s) => ({ ...s, verdict: null }))} className="cursor-pointer border-0 bg-transparent p-0 text-[11px] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]">Undo</button>
            </div>
        );
    }

    const shown = all ? met : met.slice(0, SHOWN);
    const lots = met.length >= 5 && checked && met.length / checked > BUSY_RATE;
    return (
        <div data-suggest-standard="" data-suggest-verdict={state.verdict || undefined} className={`${CARD} ${state.verdict === "useful" ? "border-[color:var(--line-strong)]" : ""}`}>
            <span className="flex items-baseline gap-x-[8px]">
                <span className={FILE_LABEL}>{savedItem ? "Standard \u00b7 saved" : "Suggested standard"}</span>
                <span data-suggest-count="" className="ml-auto font-mono text-[10px] tabular-nums text-[color:var(--muted)]">
                    {met.length.toLocaleString()} / {checked.toLocaleString()}{" \u00b7 "}of {total.toLocaleString()}
                </span>
            </span>
            {/* What share of the checked findings it met, so a noisy one and a
                quiet one read differently before a word is read. */}
            <span aria-hidden="true" className="relative block h-[2px] overflow-hidden bg-[color:var(--line-soft)]">
                <span className="absolute inset-y-0 left-0" style={{ width: met.length ? `max(2px, ${Math.min(100, (met.length / Math.max(1, checked)) * 100)}%)` : 0, background: lots ? "var(--danger, #c0392b)" : "var(--ink)" }} />
            </span>
            <span className="pt-[2px] text-[13px] leading-[1.35] font-medium text-[color:var(--ink)]">{details?.name}</span>
            <span className="text-[12px] leading-[1.45] text-[color:var(--muted)] [overflow-wrap:anywhere]">{text}</span>
            {details?.why ? <span className={`${MUTED} italic`}>Why: {details.why}</span> : null}

            <span data-suggest-found={met.length} className={`${FILE_LABEL} pt-[6px]`}>
                {met.length ? `Found ${met.length.toLocaleString()}` : "Found nothing"} in {checked.toLocaleString()} of {total.toLocaleString()} findings
                {lots ? <span className="text-[color:var(--danger,#c0392b)]">{" \u00b7 "}that may be a lot to look at</span> : null}
            </span>
            {met.length ? (
                <ul className="m-0 grid list-none p-0">
                    {shown.map((r) => {
                        const isWrong = state.wrong.includes(r.id);
                        return (
                            <li key={r.id} data-suggest-hit={r.id} className="group/hit flex items-start gap-[8px] border-t border-[color:var(--line-soft)] px-[4px] py-[5px]">
                                <span className="grid min-w-0 flex-1 gap-[1px]">
                                    <span className={`truncate text-[12px] leading-[1.35] ${isWrong ? "text-[color:var(--muted)] line-through" : "text-[color:var(--ink)]"}`}>{r.title || "Untitled finding"}</span>
                                    {r.why ? <span className={`${MUTED} [overflow-wrap:anywhere]`}>{r.why}</span> : null}
                                </span>
                                <button type="button" data-suggest-wrong={r.id} aria-pressed={isWrong} disabled={state.refined} onClick={() => toggleWrong(r.id)} className={`${BUTTON} shrink-0 whitespace-nowrap ${isWrong ? PICKED : "border-transparent text-[color:var(--muted)] group-hover/hit:border-[color:var(--line-strong)]"}`}>
                                    Shouldn&rsquo;t match
                                </button>
                            </li>
                        );
                    })}
                    {met.length > SHOWN ? (
                        <button type="button" onClick={() => setAll((v) => !v)} className="justify-self-start cursor-pointer border-0 bg-transparent p-0 text-[11px] text-[color:var(--muted)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]">
                            {all ? "Show fewer" : `Show all ${met.length.toLocaleString()}`}
                        </button>
                    ) : null}
                </ul>
            ) : (
                <span className={MUTED}>None of them meet it. That can be fine for something rare. Check more findings to be sure, or save it to watch new ones.</span>
            )}
            {recheck.length ? (
                <div data-suggest-recheck="" className="grid pt-[6px]">
                    <span className={`${FILE_LABEL} pb-[2px]`}>The findings you said it shouldn&rsquo;t match</span>
                    {recheck.map((r) => (
                        <span key={r.id} data-suggest-recheck-row={r.outcome === "met" ? "still" : "fixed"} className="flex items-baseline gap-[8px] border-t border-[color:var(--line-soft)] px-[4px] py-[5px] text-[12px] leading-[1.35]">
                            <span className="min-w-0 flex-1 truncate text-[color:var(--ink)]">{r.title || "Untitled finding"}</span>
                            <span className={`shrink-0 ${r.outcome === "met" ? "text-[color:var(--danger,#c0392b)]" : "text-[color:var(--muted)]"}`}>
                                {r.outcome === "met" ? "still matches" : r.outcome === "error" ? "couldn\u2019t check" : "no longer matches"}
                            </span>
                        </span>
                    ))}
                </div>
            ) : null}
            {failed ? <span className={MUTED}>{failed} couldn&rsquo;t be checked.</span> : null}
            {error ? <span role="alert" className="text-[11px] leading-[1.4] text-[color:var(--danger,#c0392b)]">{error}</span> : null}

            <div className="flex flex-wrap items-center gap-[6px] border-t border-[color:var(--line-soft)] pt-[6px]">
                {running ? (
                    <>
                        <span className={MUTED}>Checking {running.done} of {running.total}{"…"}</span>
                        <button type="button" onClick={stop} className={BUTTON}>Stop</button>
                    </>
                ) : (
                    <button type="button" data-suggest-more="" onClick={checkMore} disabled={!ctx || !left} className={BUTTON} title={!ctx ? "Open the Triage tab to check more findings" : !left ? "Every finding is checked" : undefined}>
                        {moreLabel(left)}
                    </button>
                )}
                <span className="flex-1" />
                <button type="button" data-suggest-refine="" onClick={refine} disabled={!reply || busy || state.refined} className={BUTTON} title={state.refined ? "Asked; the refinements come back in the chat" : "Ask for a few ways to refine it"}>
                    {state.refined ? "Refine asked" : wrong.length ? `Refine (${wrong.length} wrong)` : "Refine"}
                </button>
                <button type="button" data-suggest-useful="" onClick={() => setState((s) => ({ ...s, verdict: s.verdict === "useful" ? null : "useful" }))} className={`${BUTTON} ${state.verdict === "useful" ? PICKED : ""}`}>Useful</button>
                <button type="button" data-suggest-not-useful="" onClick={() => setState((s) => ({ ...s, verdict: "not" }))} className={BUTTON}>Not useful</button>
            </div>

            {state.verdict === "useful" ? (
                <div data-suggest-save="" className="grid gap-[6px] border-t border-[color:var(--line-soft)] pt-[8px]">
                    {savedItem ? (
                        <span data-suggest-saved="" className="text-[12px] leading-[1.4] text-[color:var(--ink)]">
                            Saved as {savedItem.name} and turned on.
                            {state.backfilled ? <span className="text-[color:var(--muted)]"> Checking older findings in {windowName}; progress shows in the Triage tab.</span> : null}
                        </span>
                    ) : !ctx ? (
                        <span className={MUTED}>Open the Triage tab to save it.</span>
                    ) : !ctx.canWrite ? (
                        <span className={MUTED}>Saving a standard needs permission to change this organization&rsquo;s settings.</span>
                    ) : (
                        <>
                            <label className="grid gap-[2px] text-[11px] text-[color:var(--muted)]">
                                Name
                                <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} className={FIELD} />
                            </label>
                            <Checkbox checked={backfill} onChange={setBackfill} data-suggest-backfill="" label={<>Also check older findings ({windowName})</>} className="justify-self-start" />
                            <span>
                                <button type="button" data-suggest-save-go="" onClick={save} disabled={saving || !name.trim()} className={`${BUTTON} ${PICKED}`}>
                                    {saving ? "Saving…" : "Save and turn on"}
                                </button>
                            </span>
                        </>
                    )}
                </div>
            ) : null}
        </div>
    );
}
